(function () {
  'use strict';

  var HERO_SELECTOR = '[data-about-wordmark-hero]';
  var STORY_SELECTOR = '[data-about-start-story]';
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobile = window.matchMedia('(max-width: 700px)');
  var HEADER_EXIT_START = 0.18;
  var HEADER_EXIT_END = 0.22;
  var MILESTONE_PAN_START = 0.22;
  var MILESTONE_PAN_END = 0.88;
  var MILESTONE_REVEAL_START = 0.22;
  var MILESTONE_REVEAL_DURATION = 0.012;
  var TIMELINE_EXIT_START = 0.95;
  var TIMELINE_EXIT_END = 0.985;
  var MANIFESTO_HOLD_VIEWPORTS = 1.1;

  function clamp(value) {
    return Math.max(0, Math.min(1, value));
  }

  function smoothstep(start, end, value) {
    var progress = clamp((value - start) / (end - start));
    return progress * progress * (3 - (2 * progress));
  }

  function mix(from, to, progress) {
    return from + ((to - from) * progress);
  }

  function pixels(value) {
    return value.toFixed(2) + 'px';
  }

  function number(value, fallback) {
    var parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function colorChannels(value) {
    var channels = value.match(/[\d.]+/g);
    return channels && channels.length >= 3
      ? [Number(channels[0]), Number(channels[1]), Number(channels[2])]
      : [90, 96, 114];
  }

  function mixedColor(from, to, progress) {
    return 'rgb(' +
      Math.round(mix(from[0], to[0], progress)) + ', ' +
      Math.round(mix(from[1], to[1], progress)) + ', ' +
      Math.round(mix(from[2], to[2], progress)) + ')';
  }

  function milestoneVisibility(centerX, cardWidth, contentHeight) {
    var viewportWidth = Math.max(1, window.innerWidth);
    var distanceFromCenter = centerX - (viewportWidth / 2);
    var readingLoad = clamp((contentHeight - 72) / 180);
    var focusRadius = Math.min(
      viewportWidth * 0.105,
      Math.max(
        42,
        (cardWidth * 0.045) + (viewportWidth * (0.018 + (readingLoad * 0.02)))
      )
    );
    var entryDistance = Math.max(
      viewportWidth * 0.22,
      cardWidth * (0.28 + (readingLoad * 0.04))
    );
    var exitDistance = Math.max(
      viewportWidth * 0.18,
      cardWidth * (0.24 + (readingLoad * 0.06))
    );
    var entry = 1 - smoothstep(
      focusRadius,
      focusRadius + entryDistance,
      Math.max(0, distanceFromCenter)
    );
    var exit = 1 - smoothstep(
      focusRadius,
      focusRadius + exitDistance,
      Math.max(0, distanceFromCenter * -1)
    );

    return {
      entry: entry,
      visibility: entry * exit
    };
  }

  function initialize(hero) {
    if (hero.dataset.aboutStartInitialized === 'true') return;

    var story = hero.nextElementSibling;
    if (!story || !story.matches(STORY_SELECTOR)) return;

    var heroSticky = hero.querySelector('.about-wordmark-hero__sticky');
    var storySticky = story.querySelector('.about-start-story__sticky');
    var storyHeader = story.querySelector('.about-start-story__header');
    var sourceWord = hero.querySelector('[data-about-start-source]');
    var targetWord = story.querySelector('[data-about-start-target]');
    var sourceLine = hero.querySelector('[data-about-source-line]');
    var targetLine = story.querySelector('[data-about-target-line]');
    var track = story.querySelector('[data-about-story-track]');
    var manifesto = story.querySelector('[data-about-story-manifesto]');
    var milestones = Array.prototype.slice.call(story.querySelectorAll('[data-about-milestone]'));
    if (!heroSticky || !storySticky || !storyHeader || !sourceWord || !targetWord || !sourceLine || !targetLine || !track || !manifesto || !milestones.length) {
      story.classList.add('is-static');
      return;
    }

    hero.dataset.aboutStartInitialized = 'true';
    story.classList.remove('is-static');

    var sharedWord = document.createElement('span');
    var sharedLine = document.createElement('span');
    var geometry = null;
    var frame = null;

    sharedWord.className = 'about-start-story__shared-word';
    sharedWord.textContent = sourceWord.textContent;
    sharedWord.setAttribute('aria-hidden', 'true');
    sharedLine.className = 'about-start-story__shared-line';
    sharedLine.setAttribute('aria-hidden', 'true');
    document.body.appendChild(sharedWord);
    document.body.appendChild(sharedLine);

    function measure() {
      var sourceRect = sourceWord.getBoundingClientRect();
      var sourceLineRect = sourceLine.getBoundingClientRect();
      var headerTransform = storyHeader.style.transform;
      var trackTransform = track.style.transform;
      storyHeader.style.transform = 'none';
      track.style.transform = 'none';
      var stickyRect = storySticky.getBoundingClientRect();
      var targetRect = targetWord.getBoundingClientRect();
      var targetLineRect = targetLine.getBoundingClientRect();
      var milestoneCenters = [];
      var cardWidths = [];
      var contentHeights = [];
      var sourceStyle = window.getComputedStyle(sourceWord);
      var targetStyle = window.getComputedStyle(targetWord);
      var sourceSize = number(sourceStyle.fontSize, 16);
      var targetSize = number(targetStyle.fontSize, sourceSize);

      milestones.forEach(function (milestone) {
        var milestoneRect = milestone.getBoundingClientRect();
        var card = milestone.querySelector('.about-start-story__card');
        var copy = milestone.querySelector('.about-start-story__copy');
        milestoneCenters.push(milestoneRect.left + (milestoneRect.width / 2));
        cardWidths.push(card ? card.offsetWidth : milestoneRect.width);
        contentHeights.push(copy ? copy.scrollHeight : 0);
      });

      storyHeader.style.transform = headerTransform;
      track.style.transform = trackTransform;

      geometry = {
        wordFromX: sourceRect.left,
        wordFromY: sourceRect.top,
        wordToX: targetRect.left,
        wordToY: targetRect.top - stickyRect.top,
        sourceFontSize: sourceSize,
        targetFontSize: targetSize,
        sourceFontWeight: number(sourceStyle.fontWeight, 400),
        targetFontWeight: number(targetStyle.fontWeight, 700),
        sourceLetterSpacing: number(sourceStyle.letterSpacing, 0),
        targetLetterSpacing: number(targetStyle.letterSpacing, 0),
        sourceLineHeight: number(sourceStyle.lineHeight, sourceSize * 1.58),
        targetLineHeight: number(targetStyle.lineHeight, targetSize * 0.96),
        sourceColor: colorChannels(sourceStyle.color),
        targetColor: colorChannels(targetStyle.color),
        lineFromX: sourceLineRect.left,
        lineFromY: sourceLineRect.top,
        lineFromWidth: sourceLineRect.width,
        lineToX: targetLineRect.left,
        lineToY: targetLineRect.top - stickyRect.top,
        lineToWidth: targetLineRect.width,
        milestoneCenters: milestoneCenters,
        cardWidths: cardWidths,
        contentHeights: contentHeights,
        panDistance: Math.max(
          0,
          milestoneCenters[milestoneCenters.length - 1] - (window.innerWidth / 2)
        )
      };

      sharedWord.style.fontFamily = targetStyle.fontFamily;
      sharedWord.style.fontStyle = sourceStyle.fontStyle;
      sharedLine.style.width = pixels(geometry.lineFromWidth);
    }

    function resetStaticState() {
      heroSticky.style.opacity = '';
      storyHeader.style.opacity = '';
      storyHeader.style.transform = '';
      sourceWord.style.visibility = 'visible';
      targetWord.style.visibility = 'visible';
      sourceLine.style.visibility = 'visible';
      targetLine.style.visibility = 'visible';
      targetWord.style.opacity = '';
      targetLine.style.opacity = '';
      sharedWord.style.visibility = 'hidden';
      sharedLine.style.visibility = 'hidden';
      sharedWord.style.opacity = '';
      sharedLine.style.opacity = '';
      track.style.transform = '';
      track.style.opacity = '';
      manifesto.style.opacity = '';
      manifesto.style.transform = '';

      milestones.forEach(function (milestone) {
        milestone.style.opacity = '';
        var card = milestone.querySelector('.about-start-story__card');
        if (card) {
          card.style.opacity = '';
          card.style.transform = '';
        }
      });
    }

    function renderMilestones(progress) {
      var pan = smoothstep(MILESTONE_PAN_START, MILESTONE_PAN_END, progress);
      var timelineReveal = smoothstep(
        MILESTONE_REVEAL_START,
        MILESTONE_REVEAL_START + MILESTONE_REVEAL_DURATION,
        progress
      );
      var timelineExit = smoothstep(TIMELINE_EXIT_START, TIMELINE_EXIT_END, progress);
      track.style.transform = 'translate3d(' + pixels(geometry.panDistance * -pan) + ',0,0)';
      track.style.opacity = (1 - timelineExit).toFixed(4);

      milestones.forEach(function (milestone, index) {
        var centerX = geometry.milestoneCenters[index] - (geometry.panDistance * pan);
        var visibility = milestoneVisibility(
          centerX,
          geometry.cardWidths[index],
          geometry.contentHeights[index]
        );
        var reveal = visibility.entry * timelineReveal;
        var cardVisibility = visibility.visibility * timelineReveal;
        var card = milestone.querySelector('.about-start-story__card');
        var direction = milestone.classList.contains('about-start-story__milestone--above') ? 1 : -1;

        milestone.style.opacity = reveal.toFixed(4);
        if (card) {
          card.style.opacity = cardVisibility.toFixed(4);
          card.style.transform = 'translateX(-50%) translate3d(0,' + pixels((1 - reveal) * direction * 34) + ',0)';
        }
      });
    }

    function render() {
      frame = null;

      if (reducedMotion.matches || mobile.matches) {
        resetStaticState();
        return;
      }

      var storyRect = story.getBoundingClientRect();
      var viewportHeight = Math.max(1, window.innerHeight);
      var travel = Math.max(1, story.offsetHeight - viewportHeight);
      var manifestoHoldDistance = viewportHeight * MANIFESTO_HOLD_VIEWPORTS;
      var activeTravel = Math.max(1, travel - manifestoHoldDistance);
      var progress = clamp(-storyRect.top / activeTravel);
      var handoff = smoothstep(0.03, 0.16, progress);
      var heroExit = smoothstep(0.055, 0.16, progress);
      var storyReveal = smoothstep(0.075, 0.16, progress);
      var headerExit = smoothstep(HEADER_EXIT_START, HEADER_EXIT_END, progress);
      var manifestoReveal = smoothstep(0.955, 0.995, progress);
      var storyIsPinned = storyRect.top <= 0;
      var storyFinished = progress >= 0.995;

      heroSticky.style.opacity = storyIsPinned ? (1 - heroExit).toFixed(4) : '1';
      storyHeader.style.opacity = storyIsPinned
        ? (storyReveal * (1 - headerExit) * (1 - manifestoReveal)).toFixed(4)
        : '0';
      storyHeader.style.transform = 'translate3d(0,' + pixels(((1 - storyReveal) - headerExit) * 26) + ',0)';
      manifesto.style.opacity = manifestoReveal.toFixed(4);
      manifesto.style.transform = 'translate3d(-50%,calc(-50% + ' + pixels((1 - manifestoReveal) * 32) + '),0)';

      if (!storyIsPinned) {
        measure();
        sourceWord.style.visibility = 'visible';
        sourceLine.style.visibility = 'visible';
        targetWord.style.visibility = 'hidden';
        targetLine.style.visibility = 'hidden';
        sharedWord.style.visibility = 'hidden';
        sharedLine.style.visibility = 'hidden';
        renderMilestones(0);
        return;
      }

      if (!geometry) measure();

      sourceWord.style.visibility = handoff <= 0 ? 'visible' : 'hidden';
      sourceLine.style.visibility = handoff <= 0 ? 'visible' : 'hidden';
      targetWord.style.visibility = storyFinished ? 'visible' : 'hidden';
      targetLine.style.visibility = storyFinished ? 'visible' : 'hidden';
      sharedWord.style.visibility = handoff > 0 && headerExit < 1 && !storyFinished ? 'visible' : 'hidden';
      sharedLine.style.visibility = handoff > 0 && !storyFinished ? 'visible' : 'hidden';
      sharedWord.style.opacity = ((1 - headerExit) * (1 - manifestoReveal)).toFixed(4);
      sharedLine.style.opacity = (1 - manifestoReveal).toFixed(4);
      targetWord.style.opacity = (1 - manifestoReveal).toFixed(4);
      targetLine.style.opacity = (1 - manifestoReveal).toFixed(4);

      sharedWord.style.transform = 'translate3d(' +
        pixels(mix(geometry.wordFromX, geometry.wordToX, handoff)) + ',' +
        pixels(mix(geometry.wordFromY, geometry.wordToY, handoff)) + ',0)';
      sharedWord.style.fontSize = pixels(mix(geometry.sourceFontSize, geometry.targetFontSize, handoff));
      sharedWord.style.fontWeight = String(Math.round(mix(geometry.sourceFontWeight, geometry.targetFontWeight, handoff)));
      sharedWord.style.letterSpacing = pixels(mix(geometry.sourceLetterSpacing, geometry.targetLetterSpacing, handoff));
      sharedWord.style.lineHeight = pixels(mix(geometry.sourceLineHeight, geometry.targetLineHeight, handoff));
      sharedWord.style.color = mixedColor(geometry.sourceColor, geometry.targetColor, handoff);

      sharedLine.style.width = pixels(mix(geometry.lineFromWidth, geometry.lineToWidth, handoff));
      sharedLine.style.transform = 'translate3d(' +
        pixels(mix(geometry.lineFromX, geometry.lineToX, handoff)) + ',' +
        pixels(mix(geometry.lineFromY, geometry.lineToY, handoff)) + ',0)';

      renderMilestones(progress);
    }

    function requestRender() {
      if (frame === null) frame = window.requestAnimationFrame(render);
    }

    function handleResize() {
      geometry = null;
      requestRender();
    }

    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', handleResize);
    if (reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', handleResize);
      mobile.addEventListener('change', handleResize);
    } else {
      reducedMotion.addListener(handleResize);
      mobile.addListener(handleResize);
    }

    if (document.fonts && document.fonts.ready) document.fonts.ready.then(handleResize);

    measure();
    requestRender();

    hero._aboutStartCleanup = function () {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', requestRender);
      window.removeEventListener('resize', handleResize);
      if (reducedMotion.removeEventListener) {
        reducedMotion.removeEventListener('change', handleResize);
        mobile.removeEventListener('change', handleResize);
      } else {
        reducedMotion.removeListener(handleResize);
        mobile.removeListener(handleResize);
      }
      resetStaticState();
      sharedWord.remove();
      sharedLine.remove();
      delete hero.dataset.aboutStartInitialized;
      delete hero._aboutStartCleanup;
    };
  }

  function initializeAll(root) {
    var scope = root || document;
    scope.querySelectorAll(HERO_SELECTOR).forEach(initialize);
    scope.querySelectorAll(STORY_SELECTOR).forEach(function (story) {
      if (!story.previousElementSibling || !story.previousElementSibling.matches(HERO_SELECTOR)) {
        story.classList.add('is-static');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initializeAll(); });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initializeAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var hero = event.target.querySelector(HERO_SELECTOR);
    if (hero && hero._aboutStartCleanup) hero._aboutStartCleanup();
  });
})();
